import * as React from "react";
import {
    BookingStatus,
    DateFormats,
    IAppointmentChecks,
    IClient,
    IClientBooking,
    IClinician,
    IIntakeAvailability,
    IIntakeSessionHistory,
    ISessionAttendee,
    RecurrenceRule,
} from "intake_shared/src/types";
import styles from "./AppointmentConfirmedDetails.module.scss";
import {
    TSaveClient,
    TUpdateClient,
    TUpdateClientByStatus,
} from "../../../clientUiTypes";
import {
    fetchCliniciansByIds,
    getClinicianByEmail,
} from "../../../../../../store/clinician";
import { CacheContext } from "../../../../../../contexts/CacheContext";
import EmptyListState from "../../../../bits/EmptyListState/EmptyListState";
import {
    findFeeByPracticeId,
    findLabelByPracticeId,
    findPracticeIdByLabel,
    toTitleCase,
} from "../../../../../../utils/utils";
import moment from "../../../../../../utils/app_moment";
import { Badge } from "../../../../bits/Badge/Badge";
import { getV2BadgePropsByPractice } from "../../../../../../constants";
import { getApplicableFee, isFeeDowngraded } from "intake_shared/src/fees";
import { Button } from "../../../../bits/Button/Button";
import CancelIcon from "../../../../icons/Cancel";
import { Dialog } from "../../../../bits/Dialog/Dialog";
import CompleteClientFlow, {
    APPOINTMENT_CANCELLATION_REASONS,
    CompleteClientFlowRef,
} from "../../../client-forms/CompleteClientFlow/CompleteClientFlow";
import store from "../../../../../../store";
import ChangeBookingDetails from "../ChangeBookingDetails/ChangeBookingDetails";
import EditIcon from "../../../../icons/Edit";
import ServiceIcon from "../../../../icons/Service";
import ClockIcon from "../../../../icons/Clock";
import LocationIcon from "../../../../icons/Location";
import { InfoIcon } from "../../../../icons/Info";
import { OverlayTrigger, Tooltip } from "react-bootstrap";
import { useToasts } from "react-toast-notifications";
import SelectIntakeSessionEvaluatorDialog from "../../SelectIntakeSessionEvaluatorDialog/SelectIntakeSessionEvaluatorDialog";
import Loader from "../../../../bits/Loader/Loader";
import { AclContext } from "../../../../../../contexts/AclContext";
import { TextArea } from "../../../../bits/TextArea/TextArea";
import PlusCircleIcon from "../../../../icons/PlusCircle";
import {
    getIntakeSessionStatus,
    isEligibleForIntakeSession,
    safeToDate,
    loadSupervisorsWithIntake as loadSupervisorsWithIntakeUtil,
} from "../../../../../v2utils";
import { arrayUnion } from "firebase/firestore";
import { getIntakeEmailSignature } from "../../../../../../utils/intakeEmailSignature";
import {
    buildEmptySessionAttendee,
    getAttendeeDob,
    getAttendeeFullName,
    getCollateralConsentAppointmentCheck,
    getConsentRelevantSessionAttendees,
    getSessionAttendeeMissingFields,
    getUpdatedAttendeeConsentCheck,
    isCollateralConsentService,
    isMinorDob,
    isSessionAttendeeSupportedService,
} from "../../../../../../utils/session_attendee_consent";
import {
    getAppointmentConsentForm,
    getAppointmentConsentFormType,
    getHeldAppointmentCheck,
} from "../../../../../../utils/consent_form";
import {
    sendAppointmentConsentFormEmail,
    sendCollateralConsentFormEmail,
} from "../../../../../../utils/queue_email";
import {
    canAddConfiguredOtherParentGuardian,
    getOtherParentGuardianClientFieldChanges,
    reconcileOtherParentConsentAttendee,
} from "../../../../../../utils/guardian";
import { isClientMinor } from "../../../../../../utils/age";
import { addSessionAttendeesHistoryEntry } from "../../../../../../store/client";
import { DatePicker } from "../../../../bits/DatePicker/DatePicker";
import SendIcon from "../../../../icons/Send";
import { FormCheckbox } from "../../../../bits/FormCheckbox/FormCheckbox";
import { Input } from "../../../../bits/Input/Input";
import Table from "../../../../bits/Table/Table";

type ConfirmedConsentRow = {
    id: string;
    type: "client" | "attendee";
    name: string;
    email: string;
    relationship: string;
    goodFormOnFile: boolean;
    sentAt: Date | null;
    reminderCount: number;
    reminderSentAt: Date | null;
    isMinor: boolean;
    dob: string | null;
    missingFields: string[];
    attendeeData: ISessionAttendee | undefined;
};

interface UpdatedConfirmedBookingsProps extends IClientBooking {
    clinician_detail: IClinician[];
}

interface AppointmentConfirmedDetailsProps {
    client: IClient;
    updateClient: TUpdateClient;
    updateClientByStatus: TUpdateClientByStatus;
    onSaveClient: TSaveClient;
}

export default function AppointmentConfirmedDetails({
    client,
    updateClient,
    updateClientByStatus,
    onSaveClient,
}: AppointmentConfirmedDetailsProps) {
    const [showCancelModal, setShowCancelModal] = React.useState(false);
    const { addToast } = useToasts();
    const completeClientFlowRef = React.useRef<CompleteClientFlowRef>(null);
    const [selectedAppointmentForCancel, setSelectedAppointmentForCancel] =
        React.useState<IClientBooking | undefined>(undefined);
    const confirmedBookings = client?.confirmed_bookings?.filter((item) => item.id);
    const [newBookingDetails, setNewBookingDetails] = React.useState<
        UpdatedConfirmedBookingsProps[]
    >([]);
    const olc = React.useContext(CacheContext);
    const allEmailSignatures = olc?.emailSignatures || {};
    const allConsentForms = olc?.consentForms || {};
    const loggedInUserEmail = React.useContext(AclContext)?.authUser.user?.email;
    const allDepartmentsAndServices = olc?.allDepartmentAndServices || {};
    const signatureEmail = client.assigned_to || client.secondary_assignee || "";
    const confirmedApptSignature = allEmailSignatures[signatureEmail];
    const [showChangeBookingModal, setShowChangeBookingModal] =
        React.useState(false);
    const [selectedBookingForChangeDetails, setSelectedBookingForChangeDetails] =
        React.useState<UpdatedConfirmedBookingsProps | null>(null);
    const [selectedBookingForIntakeAction, setSelectedBookingForIntakeAction] =
        React.useState<UpdatedConfirmedBookingsProps | null>(null);
    const [showCancelIntakeDialog, setShowCancelIntakeDialog] =
        React.useState(false);
    const [showIntakeSessionDialog, setShowIntakeSessionDialog] =
        React.useState(false);
    const [intakeActionReason, setIntakeActionReason] = React.useState("");
    const [supervisors, setSupervisors] = React.useState<IClinician[]>([]);
    const [intakeAvailabilityBySupervisor, setIntakeAvailabilityBySupervisor] =
        React.useState<{ [key: string]: IIntakeAvailability[] }>({});
    const [showIntakeSessionLoader, setShowIntakeSessionLoader] =
        React.useState(false);

    const [attendeeSaveLoading, setAttendeeSaveLoading] = React.useState(false);
    const [editingAttendeeIds, setEditingAttendeeIds] = React.useState<Set<string>>(
        new Set()
    );
    const [draftSessionAttendees, setDraftSessionAttendees] = React.useState<{
        [bookingId: string]: ISessionAttendee[];
    }>({});
    const [sendingConsentAttendeeId, setSendingConsentAttendeeId] = React.useState<
        string | null
    >(null);
    const [consentFormSendingBookingId, setConsentFormSendingBookingId] =
        React.useState<string | null>(null);
    const [goodFormOnFileLoadingBookingId, setGoodFormOnFileLoadingBookingId] =
        React.useState<string | null>(null);

    const getClinicianDetails = async () => {
        const clinicianIds = confirmedBookings.map(
            (booking) => booking.clinician_id
        );
        const clinicianDetailPromises = clinicianIds.map((clinicianId) =>
            fetchCliniciansByIds([clinicianId])
        );

        const clinicianDetails = await Promise.all(clinicianDetailPromises);

        const updatedConfirmedBookings = confirmedBookings.map((booking, index) => ({
            ...booking,
            clinician_detail: clinicianDetails[index],
        }));
        setNewBookingDetails(updatedConfirmedBookings);
    };

    React.useEffect(() => {
        getClinicianDetails();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [client]);

    React.useEffect(() => {
        setDraftSessionAttendees(
            Object.fromEntries(
                (client.confirmed_bookings || []).map((booking) => [
                    booking.id,
                    reconcileOtherParentConsentAttendee(
                        client,
                        booking.session_attendees || []
                    ),
                ])
            )
        );
    }, [client]);

    const findBookingForAttendee = (
        attendeeId: string
    ): UpdatedConfirmedBookingsProps | undefined => {
        return newBookingDetails.find((b) =>
            (draftSessionAttendees[b.id] || b.session_attendees || []).some(
                (a) => a.id === attendeeId
            )
        );
    };

    const handleAddSessionAttendee = (bookingId: string) => {
        const booking = newBookingDetails.find((b) => b.id === bookingId);
        const currentAttendees = draftSessionAttendees[bookingId] || [];
        const canAddConfiguredGuardian = Boolean(
            booking && canAddConfiguredOtherParentGuardian(client, currentAttendees)
        );

        const defaults: Partial<ISessionAttendee> = canAddConfiguredGuardian
            ? {
                  first_name: client.other_parent?.trim() || "",
                  last_name: client.other_parent_last_name?.trim() || "",
                  email: client.other_parent_email?.trim() || "",
                  relationship: "Parent/Guardian",
                  guardian_role: "other",
              }
            : {};

        const newAttendee = buildEmptySessionAttendee(defaults);
        setDraftSessionAttendees((prev) => ({
            ...prev,
            [bookingId]: [...(prev[bookingId] || []), newAttendee],
        }));
        setEditingAttendeeIds(
            (prev) => new Set(Array.from(prev).concat(newAttendee.id))
        );
    };

    const handleSessionAttendeeChange = (
        attendeeId: string,
        changes: Partial<ISessionAttendee>
    ) => {
        const booking = findBookingForAttendee(attendeeId);
        if (!booking) return;
        setDraftSessionAttendees((prev) => ({
            ...prev,
            [booking.id]: (prev[booking.id] || []).map((a) => {
                if (a.id !== attendeeId) return a;
                const updated = { ...a, ...changes, updated_at: new Date() };
                return {
                    ...updated,
                    consent_check: getUpdatedAttendeeConsentCheck(updated),
                };
            }),
        }));
    };

    const handleRemoveSessionAttendee = async (attendeeId: string) => {
        const booking = findBookingForAttendee(attendeeId);
        if (!booking) return;
        const previousAttendees =
            draftSessionAttendees[booking.id] || booking.session_attendees || [];
        const updatedAttendees = previousAttendees.map((attendee) =>
            attendee.id === attendeeId
                ? { ...attendee, active: false, updated_at: new Date() }
                : attendee
        );

        setDraftSessionAttendees((prev) => ({
            ...prev,
            [booking.id]: updatedAttendees,
        }));

        try {
            await saveSessionAttendees(booking.id, updatedAttendees, {}, false);
            addToast("Attendee removed", { appearance: "success" });
        } catch {
            setDraftSessionAttendees((prev) => ({
                ...prev,
                [booking.id]: previousAttendees,
            }));
        }
    };

    const handleSessionAttendeeConsentChange = async (attendeeId: string) => {
        const booking = findBookingForAttendee(attendeeId);
        if (!booking) return;
        const now = new Date();
        const updatedAttendees = (draftSessionAttendees[booking.id] || []).map(
            (a) => {
                if (a.id !== attendeeId) return a;
                const isGoodFormOnFile = Boolean(a.consent_check?.good_form_on_file);
                return {
                    ...a,
                    updated_at: now,
                    consent_check: getUpdatedAttendeeConsentCheck(a, {
                        good_form_on_file: !isGoodFormOnFile,
                        rechecked_at: !isGoodFormOnFile ? now : null,
                        rechecked_by: !isGoodFormOnFile
                            ? loggedInUserEmail || null
                            : null,
                    }),
                };
            }
        );
        await saveSessionAttendees(booking.id, updatedAttendees);
    };

    const saveSessionAttendees = async (
        bookingId: string,
        updatedAttendees: ISessionAttendee[],
        extraAppointmentCheckFields: Partial<IAppointmentChecks[string]> = {},
        showSavedToast = true
    ) => {
        const booking = newBookingDetails.find((b) => b.id === bookingId);
        if (!booking) return;

        const filteredAttendees = updatedAttendees.filter((a) => {
            const isEditing = editingAttendeeIds.has(a.id);
            return !(
                isEditing &&
                !a.first_name.trim() &&
                !a.last_name.trim() &&
                !a.email?.trim()
            );
        });
        const normalizedAttendees = filteredAttendees.map((a) => {
            const norm = {
                ...a,
                first_name: a.first_name.trim(),
                last_name: a.last_name.trim(),
                email: a.email?.trim() || "",
            };
            return { ...norm, consent_check: getUpdatedAttendeeConsentCheck(norm) };
        });
        const now = new Date();
        const updatedAppointmentCheck = {
            ...getCollateralConsentAppointmentCheck(
                client.appointment_checks?.[bookingId],
                { ...booking, session_attendees: normalizedAttendees },
                normalizedAttendees
            ),
            ...extraAppointmentCheckFields,
            session_attendees_last_updated_at: now,
            session_attendees_last_updated_by: loggedInUserEmail || null,
        };
        const updatedAppointmentChecks: IAppointmentChecks = {
            ...(client.appointment_checks || {}),
            [bookingId]: updatedAppointmentCheck,
        };

        setAttendeeSaveLoading(true);
        try {
            const saved = await store.updateBooking(
                booking.clinician_id,
                booking.client_id,
                booking.id,
                { session_attendees: normalizedAttendees },
                updatedAppointmentCheck,
                "confirmed",
                loggedInUserEmail!
            );
            if (!saved) throw new Error("Booking update failed");

            if (isClientMinor(client.date_of_birth)) {
                const guardianChanges = getOtherParentGuardianClientFieldChanges(
                    client,
                    normalizedAttendees
                );
                if (Object.keys(guardianChanges).length > 0) {
                    await store.updateClientData({
                        client_id: client.id,
                        changes: guardianChanges,
                        last_updated_by: loggedInUserEmail || "",
                    });
                }
            }

            const updatedConfirmedBookings = (client.confirmed_bookings || []).map(
                (b) =>
                    b.id === bookingId
                        ? { ...b, session_attendees: normalizedAttendees }
                        : b
            );
            onSaveClient({
                ...client,
                confirmed_bookings: updatedConfirmedBookings,
                appointment_checks: updatedAppointmentChecks,
            });
            setDraftSessionAttendees((prev) => ({
                ...prev,
                [bookingId]: normalizedAttendees,
            }));
            setEditingAttendeeIds(new Set());

            try {
                await addSessionAttendeesHistoryEntry(client.id, {
                    booking_id: bookingId,
                    saved_at: new Date(),
                    saved_by: loggedInUserEmail || "",
                    booking_status: "confirmed",
                    booking_requested_service: booking.requested_service || "",
                    attendees: normalizedAttendees,
                });
            } catch (error) {
                console.error("Failed to save session attendees history", error);
            }

            if (showSavedToast) {
                addToast("Session attendees saved", { appearance: "success" });
            }
        } catch (error) {
            addToast("Failed to save session attendees", { appearance: "error" });
            throw error;
        } finally {
            setAttendeeSaveLoading(false);
        }
    };

    const handleSendPrimaryClientConsentForm = async (
        booking: UpdatedConfirmedBookingsProps,
        appointmentCheck: IAppointmentChecks[string] | undefined
    ) => {
        const appointmentPractice = findLabelByPracticeId(
            booking.practice_id ||
                (process.env.REACT_APP_MANTLE_PRACTICE_ID as string),
            allDepartmentsAndServices
        );
        const resolvedForm = getAppointmentConsentForm(
            appointmentPractice || "",
            booking,
            allConsentForms
        );
        const consentFormType = getAppointmentConsentFormType(
            appointmentPractice || "",
            booking
        );
        if (!resolvedForm?.id) {
            addToast("No consent form is configured for this appointment.", {
                appearance: "error",
            });
            return;
        }
        const isUrgentReminder =
            Boolean(appointmentCheck?.consent_form_sent) &&
            !appointmentCheck?.consent_form_rechecked;
        setConsentFormSendingBookingId(booking.id);
        try {
            const sentAt = new Date();
            const clinicianCredential = booking.clinician_detail?.[0]?.credential;
            const form = await sendAppointmentConsentFormEmail(
                client,
                booking,
                appointmentPractice || "",
                allConsentForms,
                confirmedApptSignature,
                {
                    isUrgentReminder,
                    clinicianCredential,
                    queueEmail: isUrgentReminder
                        ? (email, status) =>
                              store.queueConsentReminderEmail(
                                  email,
                                  status,
                                  client.id,
                                  booking.id,
                                  resolvedForm,
                                  consentFormType,
                                  loggedInUserEmail!
                              )
                        : undefined,
                }
            );
            if (!form) {
                addToast("No consent form is configured for this appointment.", {
                    appearance: "error",
                });
                return;
            }
            const existingCheck = getHeldAppointmentCheck(appointmentCheck);
            const updatedCheck: IAppointmentChecks[string] = isUrgentReminder
                ? {
                      ...existingCheck,
                      consent_form: form,
                      consent_form_reminder_sent: sentAt,
                      consent_form_reminder_count:
                          (existingCheck.consent_form_reminder_count ?? 0) + 1,
                  }
                : {
                      ...existingCheck,
                      consent_form: form,
                      consent_form_sent: sentAt,
                  };
            const updatedAppointmentChecks: IAppointmentChecks = {
                ...(client.appointment_checks || {}),
                [booking.id]: updatedCheck,
            };
            await store.saveClientData(
                { ...client, appointment_checks: updatedAppointmentChecks },
                loggedInUserEmail!
            );
            onSaveClient({
                ...client,
                appointment_checks: updatedAppointmentChecks,
            });
            addToast(
                isUrgentReminder
                    ? "Consent Reminder Sent Successfully"
                    : "Consent Form Sent Successfully",
                { appearance: "success" }
            );
        } catch (error) {
            addToast(
                error instanceof Error
                    ? error.message
                    : isUrgentReminder
                    ? "Failed to send consent reminder."
                    : "Failed to send consent form.",
                { appearance: "error" }
            );
        } finally {
            setConsentFormSendingBookingId(null);
        }
    };

    const handleTogglePrimaryClientGoodFormOnFile = async (
        booking: UpdatedConfirmedBookingsProps,
        appointmentCheck: IAppointmentChecks[string] | undefined
    ) => {
        const isChecked = Boolean(appointmentCheck?.consent_form_rechecked);
        const form = getAppointmentConsentForm(
            findLabelByPracticeId(
                booking.practice_id ||
                    (process.env.REACT_APP_MANTLE_PRACTICE_ID as string),
                allDepartmentsAndServices
            ) || "",
            booking,
            allConsentForms
        );
        setGoodFormOnFileLoadingBookingId(booking.id);
        try {
            const updatedAppointmentChecks =
                await store.updateHeldAppointmentConsentCheck(
                    client.id,
                    booking.id,
                    form,
                    !isChecked,
                    loggedInUserEmail!
                );
            onSaveClient({
                ...client,
                appointment_checks: updatedAppointmentChecks,
            });
        } catch {
            addToast("Failed to update good form on file.", {
                appearance: "error",
            });
        } finally {
            setGoodFormOnFileLoadingBookingId(null);
        }
    };

    const handleSendAttendeeConsentForm = async (attendee: ISessionAttendee) => {
        if (!attendee.email?.trim()) return;
        const booking = findBookingForAttendee(attendee.id);
        if (!booking) return;
        const appointmentPractice = findLabelByPracticeId(
            booking.practice_id ||
                (process.env.REACT_APP_MANTLE_PRACTICE_ID as string),
            allDepartmentsAndServices
        );
        const consentForm = getAppointmentConsentForm(
            appointmentPractice || "",
            booking,
            allConsentForms
        );
        const isCustodian =
            isClientMinor(client.date_of_birth) &&
            attendee.guardian_role === "other";
        const isReminder = Boolean(attendee.consent_check.sent_at);
        setSendingConsentAttendeeId(attendee.id);
        try {
            await sendCollateralConsentFormEmail({
                client,
                appointment: booking,
                attendeeId: attendee.id,
                attendeeFirstName:
                    attendee.first_name?.trim() ||
                    getAttendeeFullName(attendee) ||
                    "",
                attendeeEmail: attendee.email.trim(),
                attendeeRelationship: attendee.relationship,
                attendeeIsMinor: isMinorDob(getAttendeeDob(attendee)),
                consentForm,
                signature: confirmedApptSignature,
                department: appointmentPractice || undefined,
                isCustodian,
                isReminder,
            });
            const sentAt = new Date();
            const dob = getAttendeeDob(attendee);
            const isMinor = isMinorDob(dob);
            const variant = isCustodian
                ? consentForm?.types?.find(
                      (t) =>
                          t.name === "Minors - Secondary Custodian" ||
                          t.name === "Minor - Secondary Custodian"
                  )
                : consentForm?.types?.find((t) =>
                      isMinor
                          ? t.name === "Collateral Consent MINOR"
                          : t.name === "Collateral Consent"
                  ) || consentForm?.types?.[0];
            const updatedAttendees = (draftSessionAttendees[booking.id] || []).map(
                (a) =>
                    a.id === attendee.id
                        ? {
                              ...a,
                              updated_at: sentAt,
                              consent_check: getUpdatedAttendeeConsentCheck(
                                  a,
                                  isReminder
                                      ? {
                                            reminder_sent_at: sentAt,
                                            reminder_count:
                                                (a.consent_check.reminder_count ??
                                                    0) + 1,
                                        }
                                      : {
                                            sent_at: sentAt,
                                            collateral_consent_form_id:
                                                consentForm?.id || null,
                                            collateral_consent_form_name:
                                                consentForm?.name || null,
                                            collateral_consent_form_variant_id:
                                                variant?.id || null,
                                            collateral_consent_form_variant_name:
                                                variant?.name || null,
                                        }
                              ),
                          }
                        : a
            );
            try {
                await saveSessionAttendees(
                    booking.id,
                    updatedAttendees,
                    !isReminder &&
                        !(client.appointment_checks || {})[booking.id]
                            ?.consent_form_sent
                        ? { consent_form_sent: sentAt }
                        : {},
                    false
                );
            } catch {
                addToast(
                    "Consent form was sent, but we could not save the record. Please refresh before retrying to avoid sending it again.",
                    { appearance: "error" }
                );
                return;
            }
            addToast(
                isReminder
                    ? `Consent reminder sent to ${attendee.email}`
                    : `Consent form sent to ${attendee.email}`,
                { appearance: "success" }
            );
        } catch {
            addToast("Failed to send consent form", { appearance: "error" });
        } finally {
            setSendingConsentAttendeeId(null);
        }
    };

    const setChangeBookingModalOpen: React.Dispatch<
        React.SetStateAction<boolean>
    > = (value) => {
        setShowChangeBookingModal((currentValue) => {
            const nextValue =
                typeof value === "function" ? value(currentValue) : value;
            if (!nextValue) {
                setSelectedBookingForChangeDetails(null);
            }
            return nextValue;
        });
    };

    const loadSupervisorsWithIntake = async (b: UpdatedConfirmedBookingsProps) => {
        if (!b) return;

        try {
            const clinician = b?.clinician_detail?.[0];
            if (!clinician) return;

            const { enrichedSupervisors, availability } =
                await loadSupervisorsWithIntakeUtil(
                    clinician,
                    allDepartmentsAndServices,
                    store.fetchAllSupervisors
                );

            setSupervisors(enrichedSupervisors);
            setIntakeAvailabilityBySupervisor(availability);
        } catch (error) {
            console.error(
                "Failed to load supervisors with intake availability",
                error
            );
        }
    };

    const handleReschduleIntakeSessionBooking = async (
        supervisorId: string,
        intakeSession: IIntakeAvailability
    ) => {
        const booking = selectedBookingForIntakeAction;
        if (!booking?.clinician_id || !booking?.client_id) {
            addToast("Unable to save intake session (missing IDs)", {
                appearance: "error",
            });
            return;
        }

        setShowIntakeSessionLoader(true);

        try {
            // Find the selected supervisor to determine practice_id
            const selectedSupervisor = supervisors.find(
                (sup) => sup.email === supervisorId || sup.id === supervisorId
            ) as
                | (IClinician & {
                      supervisorRole?: "primary" | "secondary";
                      supervisorPracticeLabel?: string;
                  })
                | undefined;

            let practiceId: string | undefined;
            let clinicianDisplayName: string = "";

            if (selectedSupervisor) {
                clinicianDisplayName =
                    selectedSupervisor.fullname ||
                    `${selectedSupervisor.firstname || ""} ${
                        selectedSupervisor.lastname || ""
                    }`.trim() ||
                    "";

                if (selectedSupervisor.supervisorRole === "primary") {
                    const praticeLabel =
                        booking?.clinician_detail[0]?.departments?.[0];
                    const practiceIdFromLabel = findPracticeIdByLabel(
                        praticeLabel,
                        allDepartmentsAndServices
                    );
                    practiceId = practiceIdFromLabel || "";
                } else if (selectedSupervisor.supervisorRole === "secondary") {
                    const clinician = booking?.clinician_detail[0];
                    const secondaryPair = (
                        clinician.secondary_supervisor_ids || []
                    ).find((p) => {
                        const supId = selectedSupervisor.id || "";
                        const supEmail = selectedSupervisor.email || "";
                        return (
                            p.supervisor_id === supId || p.supervisor_id === supEmail
                        );
                    });
                    if (secondaryPair) {
                        practiceId = secondaryPair.practice_id;
                    }
                } else {
                    const praticeLabel = selectedSupervisor?.departments?.[0];
                    const practiceIdFromLabel = findPracticeIdByLabel(
                        praticeLabel,
                        allDepartmentsAndServices
                    );
                    practiceId = practiceIdFromLabel || "";
                }
            }

            const resolvedIntakePracticeId =
                intakeSession.practice_id || booking.practice_id || practiceId || "";

            const previousIntake = booking?.supervisor_intake_session;

            if (client && booking && previousIntake) {
                const clientIntakeSessionHistoryEntry: IIntakeSessionHistory = {
                    ...previousIntake,
                    booking_id: booking.id,
                    supervisor_id: previousIntake.supervisor_id,
                    start_time: previousIntake.start_time,
                    end_time: previousIntake.end_time,
                    status: previousIntake.status,
                    actioned_by: loggedInUserEmail || "",
                    action: "rescheduled",
                    actioned_on: new Date(),
                    reason: intakeActionReason || "",
                    practice_id: previousIntake.practice_id || "",
                    clinician_display_name:
                        previousIntake.clinician_display_name || "",
                    service_delivery_option:
                        previousIntake.service_delivery_option || "remote",
                    location: previousIntake.location || "Remote",
                    recurrence: previousIntake.recurrence || RecurrenceRule.ONE_OFF,
                    booking_requested_service: booking.requested_service || "",
                    booking_clinician_display_name:
                        booking.clinician_display_name || "",
                    booking_start: booking.start || "",
                    booking_starting_date: booking.starting_date || "",
                    booking_practice_id: booking.practice_id || "",
                };

                try {
                    await store.updateClientData({
                        client_id: client.id,
                        changes: {
                            intake_session_history: arrayUnion(
                                clientIntakeSessionHistoryEntry
                            ),
                        },
                        last_updated_by: loggedInUserEmail || "",
                    });
                } catch (error) {
                    console.error(
                        "Failed to update client intake session history for reschedule",
                        error
                    );
                }
            }

            const intakeSessionData = {
                id: intakeSession.id,
                supervisor_id: supervisorId,
                service_delivery_option: intakeSession.service_delivery_option,
                start_time: intakeSession.start_time,
                end_time: intakeSession.end_time,
                recurrence: RecurrenceRule.ONE_OFF as RecurrenceRule.ONE_OFF,
                created_at: intakeSession.created_at || new Date(),
                location: intakeSession.location || "",
                office: intakeSession.office || "",
                status:
                    booking.status === BookingStatus.HELD
                        ? ("held" as const)
                        : ("confirmed" as const),
                completed_at: null,
                practice_id: resolvedIntakePracticeId,
                clinician_display_name: clinicianDisplayName,
                supervisees_display_name: booking?.clinician_display_name || "",
            };
            const signatureEmail =
                client.assigned_to || client.secondary_assignee || "";

            const confirmedApptSignature =
                allEmailSignatures[signatureEmail as string];

            const intakeSignature = getIntakeEmailSignature(olc);

            const success = await store.updateBooking(
                booking?.clinician_id,
                booking?.client_id,
                booking?.id,
                {
                    supervisor_intake_session: intakeSessionData,
                },
                {},
                "confirmed",
                loggedInUserEmail!,
                confirmedApptSignature,
                allDepartmentsAndServices,
                intakeSignature,
                "supervisor_intake_session"
            );

            if (!success) {
                addToast("Failed to save intake session", {
                    appearance: "error",
                });
                return;
            }

            try {
                // If updating an existing intake session, clear the old one and book the new one
                if (booking?.supervisor_intake_session) {
                    const oldSupervisorId =
                        booking?.supervisor_intake_session.supervisor_id;
                    await store.replaceIntakeSessionBooking(
                        oldSupervisorId,
                        booking?.id,
                        booking?.client_id,
                        supervisorId,
                        intakeSession.id,
                        booking?.client_display_name,
                        "confirmed",
                        resolvedIntakePracticeId,
                        booking?.clinician_display_name
                    );
                } else {
                    // New intake session, just book it
                    await store.updateIntakeSessionBooking(
                        supervisorId,
                        intakeSession.id,
                        booking?.id,
                        booking?.client_id,
                        booking?.client_display_name,
                        "confirmed",
                        resolvedIntakePracticeId,
                        booking?.clinician_display_name
                    );
                }
            } catch (error) {
                console.error(
                    "Failed to update intake session booking status",
                    error
                );
            }

            const updatedConfirmedBookings = (client.confirmed_bookings || []).map(
                (b) =>
                    b.id === booking?.id
                        ? {
                              ...b,
                              supervisor_intake_session: intakeSessionData,
                          }
                        : b
            );

            const updatedClient = {
                ...client,
                confirmed_bookings: updatedConfirmedBookings,
            };

            onSaveClient(updatedClient);

            addToast("Intake session saved", {
                appearance: "success",
            });

            setShowIntakeSessionDialog(false);
        } finally {
            setShowIntakeSessionLoader(false);
        }
    };

    const handleCancelIntakeSession = async (
        schedule: UpdatedConfirmedBookingsProps,
        reason?: string
    ) => {
        setShowIntakeSessionLoader(true);
        try {
            const previousIntake = schedule?.supervisor_intake_session;
            const getSupervisorIntakeDetails = await getClinicianByEmail(
                schedule?.supervisor_intake_session?.supervisor_id || ""
            );

            if (getSupervisorIntakeDetails) {
                const currentSessions =
                    getSupervisorIntakeDetails?.available_for_intake_sessions || [];
                const sessionToUpdate = currentSessions.find(
                    (s) => s.booking_id === schedule?.id || ""
                );

                if (sessionToUpdate) {
                    const updatedSessions = currentSessions.map((session) => {
                        if (session.id === sessionToUpdate.id) {
                            return {
                                ...session,
                                client_id: "",
                                booking_id: "",
                                practice_id: "",
                                client_display_name: "",
                                status: "available",
                            };
                        }
                        return session;
                    });

                    try {
                        await store.updateClinicianData({
                            clinician_id:
                                getSupervisorIntakeDetails.id ||
                                getSupervisorIntakeDetails.email,
                            changes: {
                                available_for_intake_sessions: updatedSessions,
                            },
                        });
                    } catch (error) {
                        console.error("Failed to update intake session", error);
                    }
                }
            }

            if (client && schedule && previousIntake) {
                const clientIntakeSessionHistoryEntry: IIntakeSessionHistory = {
                    ...previousIntake,
                    booking_id: schedule.id,
                    supervisor_id: previousIntake.supervisor_id,
                    start_time: previousIntake.start_time,
                    end_time: previousIntake.end_time,
                    status: previousIntake.status,
                    actioned_by: loggedInUserEmail || "",
                    action: "cancelled",
                    actioned_on: new Date(),
                    practice_id: previousIntake.practice_id || "",
                    clinician_display_name:
                        previousIntake.clinician_display_name || "",
                    service_delivery_option:
                        previousIntake.service_delivery_option || "remote",
                    location: previousIntake.location || "Remote",
                    recurrence: previousIntake.recurrence || RecurrenceRule.ONE_OFF,
                    reason: reason || "",
                    booking_requested_service: schedule.requested_service || "",
                    booking_clinician_display_name:
                        schedule.clinician_display_name || "",
                    booking_start: schedule.start || "",
                    booking_starting_date: schedule.starting_date || "",
                    booking_practice_id: schedule.practice_id || "",
                };

                try {
                    await store.updateClientData({
                        client_id: client.id,
                        changes: {
                            intake_session_history: arrayUnion(
                                clientIntakeSessionHistoryEntry
                            ),
                        },
                        last_updated_by: loggedInUserEmail || "",
                    });
                } catch (error) {
                    console.error(
                        "Failed to update client intake session history for reschedule",
                        error
                    );
                }
            }

            const success = await store.updateBooking(
                schedule?.clinician_id || "",
                schedule?.client_id || "",
                schedule?.id || "",
                {
                    supervisor_intake_session: null,
                },
                {},
                "confirmed",
                loggedInUserEmail!
            );

            if (!success) {
                addToast("Failed to cancel intake session", {
                    appearance: "error",
                });
                return;
            }

            const updatedConfirmedBookings = (client.confirmed_bookings || []).map(
                (b) =>
                    b.id === schedule?.id
                        ? {
                              ...b,
                              supervisor_intake_session: null,
                          }
                        : b
            );

            const updatedClient = {
                ...client,
                confirmed_bookings: updatedConfirmedBookings,
            };

            onSaveClient(updatedClient);
            addToast("Intake session cancelled", {
                appearance: "success",
            });
            setShowCancelIntakeDialog(false);
            setSelectedBookingForIntakeAction(null);
            setIntakeActionReason("");
        } finally {
            setShowIntakeSessionLoader(false);
        }
    };

    const editingAttendeeForConfirmedDialog = React.useMemo(() => {
        if (editingAttendeeIds.size === 0) return null;
        const editId = Array.from(editingAttendeeIds)[0];
        for (const booking of newBookingDetails) {
            const attendee = (draftSessionAttendees[booking.id] || []).find(
                (a) => a.id === editId
            );
            if (attendee) return { attendee, booking };
        }
        return null;
    }, [editingAttendeeIds, newBookingDetails, draftSessionAttendees]);

    return (
        <div className={styles.appointmentConfirmedContainer}>
            <Dialog
                open={showCancelModal}
                onClose={() => setShowCancelModal(false)}
                title="Cancel Appointment"
                saveText="Cancel Appointment"
                onSave={() => {
                    completeClientFlowRef.current?.handleSubmit();
                }}
                cancelText="Close"
            >
                <CompleteClientFlow
                    ref={completeClientFlowRef}
                    title="Cancel sessions"
                    clientProp={client}
                    reasons={APPOINTMENT_CANCELLATION_REASONS}
                    onSaveClient={(updatedClient, close = true) => {
                        onSaveClient(updatedClient);
                        if (close) {
                            setShowCancelModal(false);
                        }
                    }}
                    showCompletionReason
                    showCompletionNotes
                    showWaitListOption={false}
                    showAppointmentsToCancel={true}
                    appointmentToCancel={selectedAppointmentForCancel}
                    expectedCaller="admin"
                    hideCard={true}
                    viewFrom="appt_confirmed"
                />
            </Dialog>
            {newBookingDetails?.length < 1 ? (
                <EmptyListState
                    emptyMessage="No appointment confirmed"
                    stateHelperText="No confirmed appointments yet"
                />
            ) : (
                newBookingDetails.map((b) => {
                    const appointmentCheck = (client.appointment_checks || {})[b.id];
                    const consentFormSent =
                        appointmentCheck?.consent_form_sent ||
                        appointmentCheck?.consent_form_rechecked ||
                        null;
                    let consultCallCompleted: string = Boolean(
                        appointmentCheck?.consult_call_completed
                    )
                        ? "Yes"
                        : "No";
                    if (
                        consultCallCompleted &&
                        appointmentCheck?.consult_call_completed_on
                    ) {
                        consultCallCompleted = `${consultCallCompleted} (on ${moment(
                            appointmentCheck?.consult_call_completed_on
                        ).format(DateFormats.DAY_MONTH_FORMAT)})`;
                    }
                    const _consultCallRequested = Boolean(
                        appointmentCheck?.client_requested_call
                    )
                        ? "Yes"
                        : "No";
                    const _vaccinatedCheckCompleted =
                        appointmentCheck?.vaccination_checks_completed_on &&
                        `${moment(
                            appointmentCheck?.vaccination_checks_completed_on
                        ).format(DateFormats.DATE_FORMAT)}`;

                    const appointmentPractice = findLabelByPracticeId(
                        b.practice_id ||
                            (process.env.REACT_APP_MANTLE_PRACTICE_ID as string),
                        allDepartmentsAndServices
                    );
                    const supervisorPractice = findLabelByPracticeId(
                        b?.supervisor_intake_session?.practice_id || "",
                        allDepartmentsAndServices
                    );
                    const supervisorIntakeOveridden =
                        appointmentCheck?.supervisor_intake_not_required_reason;
                    const feeType =
                        findFeeByPracticeId(
                            b.practice_id,
                            allDepartmentsAndServices
                        ) || "";
                    const isEligible = isEligibleForIntakeSession(
                        client,
                        b.clinician_detail?.[0],
                        b
                    );
                    const intakeSessionStatus = getIntakeSessionStatus(
                        b?.supervisor_intake_session
                    );
                    const completedOnDate = safeToDate(
                        b.supervisor_intake_session?.completed_at
                    );
                    const applicableFee = getApplicableFee(
                        client,
                        b.clinician_detail?.[0]?.credential || "",
                        b.clinician_detail?.[0]?.email || "",
                        b.requested_service,
                        feeType
                    );
                    const feeToUse = applicableFee?.fee_to_use?.replace("$", " ");
                    const feeDowngraded = isFeeDowngraded(
                        b.clinician_detail?.[0]?.credential || "",
                        applicableFee.fee_type
                    );
                    const supportsSessionAttendees =
                        isSessionAttendeeSupportedService(b.requested_service);
                    const activeAttendees = getConsentRelevantSessionAttendees(
                        draftSessionAttendees[b.id] || b.session_attendees || [],
                        b.requested_service
                    );

                    const requiresCollateralConsent = isCollateralConsentService({
                        ...b,
                        session_attendees: activeAttendees,
                    });
                    const canAddConfiguredGuardian =
                        canAddConfiguredOtherParentGuardian(client, activeAttendees);

                    const collateralConsentMissingCount = requiresCollateralConsent
                        ? activeAttendees.filter(
                              (attendee) =>
                                  getSessionAttendeeMissingFields(
                                      attendee,
                                      b.requested_service
                                  ).length > 0 ||
                                  !attendee.consent_check?.good_form_on_file
                          ).length
                        : 0;

                    const totalConsentMissingCount =
                        (Boolean(appointmentCheck?.consent_form_rechecked) ? 0 : 1) +
                        collateralConsentMissingCount;

                    const consentTableData: ConfirmedConsentRow[] = [
                        {
                            id: "client",
                            type: "client",
                            name:
                                [client.first_name, client.last_name]
                                    .filter(Boolean)
                                    .join(" ") || "Client",
                            email: client.email || "",
                            relationship: "Primary client",
                            goodFormOnFile: Boolean(
                                appointmentCheck?.consent_form_rechecked
                            ),
                            sentAt: consentFormSent || null,
                            reminderCount:
                                appointmentCheck?.consent_form_reminder_count ?? 0,
                            reminderSentAt:
                                appointmentCheck?.consent_form_reminder_sent || null,
                            isMinor: false,
                            dob: null,
                            missingFields: [],
                            attendeeData: undefined,
                        },
                        ...(requiresCollateralConsent
                            ? activeAttendees.map((attendee) => {
                                  const dob = getAttendeeDob(attendee);
                                  return {
                                      id: attendee.id,
                                      type: "attendee" as const,
                                      name: getAttendeeFullName(attendee) || "—",
                                      email: attendee.email || "",
                                      relationship: attendee.relationship || "—",
                                      goodFormOnFile: Boolean(
                                          attendee.consent_check?.good_form_on_file
                                      ),
                                      sentAt:
                                          attendee.consent_check?.sent_at || null,
                                      reminderCount:
                                          attendee.consent_check?.reminder_count ||
                                          0,
                                      reminderSentAt:
                                          attendee.consent_check?.reminder_sent_at ||
                                          null,
                                      isMinor: isMinorDob(dob),
                                      dob: dob || null,
                                      missingFields: getSessionAttendeeMissingFields(
                                          attendee,
                                          b.requested_service
                                      ),
                                      attendeeData: attendee,
                                  };
                              })
                            : []),
                    ];

                    const consentColumns = [
                        {
                            Header: "Recipient",
                            id: "recipient",
                            accessor: (row: ConfirmedConsentRow) => (
                                <div className={styles.consentRecipientCell}>
                                    <span className={styles.consentRecipientName}>
                                        {row.name}
                                        {row.isMinor && (
                                            <span
                                                className={styles.attendeeMinorTag}
                                            >
                                                {" "}
                                                Minor
                                            </span>
                                        )}
                                    </span>
                                    <span className={styles.consentRecipientEmail}>
                                        {row.email || "No email on file"}
                                    </span>
                                    {row.dob && (
                                        <span
                                            className={styles.consentRecipientEmail}
                                        >
                                            DOB:{" "}
                                            {moment(row.dob).format(
                                                DateFormats.DAY_MONTH_YEAR_FORMAT
                                            )}
                                        </span>
                                    )}
                                </div>
                            ),
                        },
                        {
                            Header: "Relationship",
                            id: "relationship",
                            accessor: (row: ConfirmedConsentRow) => (
                                <span className={styles.consentCellText}>
                                    {row.relationship}
                                </span>
                            ),
                        },
                        {
                            Header: "Good form on file?",
                            id: "goodForm",
                            accessor: (row: ConfirmedConsentRow) => {
                                if (row.type === "client") {
                                    const rechecked =
                                        appointmentCheck?.consent_form_rechecked;
                                    return (
                                        <FormCheckbox
                                            wrapperClass={
                                                styles.recheckedCheckboxWrapper
                                            }
                                            label={
                                                rechecked
                                                    ? moment(rechecked).format(
                                                          DateFormats.DATE_FORMAT
                                                      )
                                                    : "Mark on file"
                                            }
                                            labelClass={
                                                styles.recheckedCheckboxLabel
                                            }
                                            checked={Boolean(rechecked)}
                                            disabled={
                                                goodFormOnFileLoadingBookingId ===
                                                b.id
                                            }
                                            onChange={() =>
                                                handleTogglePrimaryClientGoodFormOnFile(
                                                    b,
                                                    appointmentCheck
                                                )
                                            }
                                        />
                                    );
                                }
                                const attendee = row.attendeeData!;
                                return (
                                    <FormCheckbox
                                        wrapperClass={
                                            styles.recheckedCheckboxWrapper
                                        }
                                        label={
                                            attendee.consent_check?.rechecked_at
                                                ? moment(
                                                      attendee.consent_check
                                                          .rechecked_at
                                                  ).format(DateFormats.DATE_FORMAT)
                                                : "Mark on file"
                                        }
                                        labelClass={styles.recheckedCheckboxLabel}
                                        checked={Boolean(
                                            attendee.consent_check?.good_form_on_file
                                        )}
                                        disabled={attendeeSaveLoading}
                                        onChange={() =>
                                            handleSessionAttendeeConsentChange(
                                                attendee.id
                                            )
                                        }
                                    />
                                );
                            },
                        },
                        {
                            Header: "Status",
                            id: "mantleStatus",
                            accessor: (row: ConfirmedConsentRow) => {
                                const hasSentConsent = Boolean(
                                    appointmentCheck?.consent_form_sent ??
                                        appointmentCheck?.consent_form_reminder_sent
                                );
                                const mantleStatus =
                                    appointmentCheck?.mantle_consent_status ??
                                    (hasSentConsent ? "sent" : null);

                                if (row.type !== "client" || !mantleStatus) {
                                    return (
                                        <span className={styles.consentCellText}>
                                            &mdash;
                                        </span>
                                    );
                                }

                                const needsCorrection = mantleStatus
                                    .toLowerCase()
                                    .includes("correction");
                                const statusBadge = (
                                    <Badge
                                        content={mantleStatus
                                            .replace(/_/g, " ")
                                            .toUpperCase()}
                                        textColor={
                                            needsCorrection ? "#92400E" : "#205773"
                                        }
                                        bgColor={
                                            needsCorrection ? "#FEF3C7" : "#C6ECFF"
                                        }
                                    />
                                );

                                if (
                                    !needsCorrection ||
                                    !appointmentCheck?.correction_reason
                                ) {
                                    return statusBadge;
                                }

                                return (
                                    <span className={styles.consentActionCell}>
                                        {statusBadge}
                                        <OverlayTrigger
                                            placement="top"
                                            trigger={["hover", "focus"]}
                                            overlay={
                                                <Tooltip
                                                    id={`mantle-consent-correction-${b.id}`}
                                                >
                                                    {
                                                        appointmentCheck.correction_reason
                                                    }
                                                </Tooltip>
                                            }
                                        >
                                            <span
                                                tabIndex={0}
                                                aria-label={
                                                    "View Mantle consent " +
                                                    "correction reason"
                                                }
                                            >
                                                <InfoIcon
                                                    width="14"
                                                    height="14"
                                                    stroke="#92400E"
                                                />
                                            </span>
                                        </OverlayTrigger>
                                    </span>
                                );
                            },
                        },
                        {
                            Header: "Sent",
                            id: "sent",
                            accessor: (row: ConfirmedConsentRow) => (
                                <span className={styles.consentCellText}>
                                    {row.sentAt
                                        ? moment(row.sentAt).format(
                                              DateFormats.DAY_YEAR_HOUR_MIN_FORMAT
                                          )
                                        : "—"}
                                </span>
                            ),
                        },
                        {
                            Header: "Actions",
                            id: "actions",
                            accessor: (row: ConfirmedConsentRow) => {
                                if (row.type === "client") {
                                    const canSendClient = Boolean(
                                        client.email?.trim()
                                    );
                                    const isSendingClient =
                                        consentFormSendingBookingId === b.id;
                                    return (
                                        <div className={styles.consentActionCell}>
                                            {(appointmentCheck?.consent_form_reminder_count ??
                                                0) > 0 && (
                                                <span
                                                    className={
                                                        styles.consentCellText
                                                    }
                                                >
                                                    {
                                                        appointmentCheck!
                                                            .consent_form_reminder_count
                                                    }
                                                    x · last{" "}
                                                    {moment(
                                                        appointmentCheck!
                                                            .consent_form_reminder_sent
                                                    ).format(
                                                        DateFormats.DATE_FORMAT
                                                    )}
                                                </span>
                                            )}
                                            {canSendClient &&
                                                !appointmentCheck?.consent_form_rechecked && (
                                                    <Button
                                                        variant="normal"
                                                        iconLeft={<SendIcon />}
                                                        className={
                                                            styles.sendConsentFormButton
                                                        }
                                                        disabled={isSendingClient}
                                                        onClick={() =>
                                                            handleSendPrimaryClientConsentForm(
                                                                b,
                                                                appointmentCheck
                                                            )
                                                        }
                                                    >
                                                        {isSendingClient
                                                            ? "Sending…"
                                                            : appointmentCheck?.consent_form_sent
                                                            ? "Remind"
                                                            : "Send"}
                                                    </Button>
                                                )}
                                        </div>
                                    );
                                }
                                const attendee = row.attendeeData!;
                                const canSend =
                                    Boolean(attendee.email?.trim()) &&
                                    row.missingFields.length === 0;
                                return (
                                    <div className={styles.consentActionCell}>
                                        {(attendee.consent_check?.reminder_count ??
                                            0) > 0 && (
                                            <span className={styles.consentCellText}>
                                                {
                                                    attendee.consent_check!
                                                        .reminder_count
                                                }
                                                x · last{" "}
                                                {moment(
                                                    attendee.consent_check!
                                                        .reminder_sent_at
                                                ).format(DateFormats.DATE_FORMAT)}
                                            </span>
                                        )}
                                        {canSend && (
                                            <Button
                                                variant="normal"
                                                iconLeft={<SendIcon />}
                                                className={
                                                    styles.sendConsentFormButton
                                                }
                                                disabled={
                                                    sendingConsentAttendeeId !== null
                                                }
                                                onClick={() =>
                                                    handleSendAttendeeConsentForm(
                                                        attendee
                                                    )
                                                }
                                            >
                                                {sendingConsentAttendeeId ===
                                                attendee.id
                                                    ? "Sending…"
                                                    : attendee.consent_check?.sent_at
                                                    ? "Remind"
                                                    : "Send"}
                                            </Button>
                                        )}
                                        {row.missingFields.length > 0 && (
                                            <span
                                                className={
                                                    styles.attendeeMissingWarning
                                                }
                                            >
                                                Missing:{" "}
                                                {row.missingFields
                                                    .map((f) => f.replace(/_/g, " "))
                                                    .join(", ")}
                                            </span>
                                        )}
                                        <div className={styles.attendeeInfoActions}>
                                            <Button
                                                variant="normal"
                                                disabled={attendeeSaveLoading}
                                                onClick={() =>
                                                    setEditingAttendeeIds(
                                                        (prev) =>
                                                            new Set(
                                                                Array.from(
                                                                    prev
                                                                ).concat(attendee.id)
                                                            )
                                                    )
                                                }
                                            >
                                                <EditIcon />
                                            </Button>
                                            <Button
                                                variant="normal"
                                                disabled={attendeeSaveLoading}
                                                onClick={() =>
                                                    handleRemoveSessionAttendee(
                                                        attendee.id
                                                    )
                                                }
                                            >
                                                <CancelIcon stroke="#FB3748" />
                                            </Button>
                                        </div>
                                    </div>
                                );
                            },
                        },
                    ];

                    return (
                        <div
                            key={`appt-${b.id}`}
                            className={styles.appointmentDetailsContainer}
                        >
                            <div className={styles.headerRow}>
                                <div className={styles.clinicianInfoSection}>
                                    <div className={styles.intakeClinicianHeader}>
                                        <p className={styles.appointmentClinician}>
                                            {b.clinician_display_name}
                                        </p>
                                    </div>

                                    <div className={styles.appointmentBadges}>
                                        {b.clinician_detail?.[0]?.credential && (
                                            <Badge
                                                tone="info"
                                                content={
                                                    b.clinician_detail?.[0]
                                                        ?.credential
                                                }
                                            />
                                        )}
                                        <Badge
                                            content={appointmentPractice}
                                            textColor={
                                                getV2BadgePropsByPractice(
                                                    appointmentPractice
                                                ).textColor
                                            }
                                            bgColor={
                                                getV2BadgePropsByPractice(
                                                    appointmentPractice
                                                ).bgColor
                                            }
                                        />
                                    </div>
                                </div>
                                <div className={styles.apptActions}>
                                    {isEligible && !b.supervisor_intake_session && (
                                        <OverlayTrigger
                                            placement="bottom"
                                            overlay={
                                                <Tooltip id="age-tooltip">
                                                    Add Supervisor Intake Session
                                                </Tooltip>
                                            }
                                        >
                                            <Button
                                                variant="secondary"
                                                size="icon"
                                                aria-label="Add intake session"
                                                onClick={async () => {
                                                    await loadSupervisorsWithIntake(
                                                        b
                                                    );
                                                    setSelectedBookingForIntakeAction(
                                                        b
                                                    );
                                                    setShowIntakeSessionDialog(true);
                                                }}
                                            >
                                                <PlusCircleIcon />
                                            </Button>
                                        </OverlayTrigger>
                                    )}
                                    <OverlayTrigger
                                        placement="bottom"
                                        overlay={
                                            <Tooltip id="age-tooltip">
                                                Cancel Appointment
                                            </Tooltip>
                                        }
                                    >
                                        <Button
                                            variant="danger"
                                            mode="outline"
                                            size="icon"
                                            aria-label="Cancel appointment"
                                            onClick={() => {
                                                setSelectedAppointmentForCancel(b);
                                                setShowCancelModal(true);
                                            }}
                                        >
                                            <CancelIcon />
                                        </Button>
                                    </OverlayTrigger>
                                    <OverlayTrigger
                                        placement="bottom"
                                        overlay={
                                            <Tooltip id="age-tooltip">
                                                Change Booking
                                            </Tooltip>
                                        }
                                    >
                                        <Button
                                            variant="secondary"
                                            size="icon"
                                            aria-label="Change booking"
                                            onClick={() => {
                                                setSelectedBookingForChangeDetails(
                                                    b
                                                );
                                                setShowChangeBookingModal(true);
                                            }}
                                        >
                                            <EditIcon />
                                        </Button>
                                    </OverlayTrigger>
                                </div>
                            </div>

                            <div className={styles.detailGrid}>
                                <div className={styles.detailItem}>
                                    <div className={styles.detailIcon}>
                                        <ServiceIcon />
                                    </div>
                                    <div className={styles.detailContent}>
                                        <div className={styles.detailLabel}>
                                            Service
                                        </div>
                                        <div className={styles.detailValue}>
                                            {b.requested_service}
                                        </div>
                                        <div className={styles.detailSubtext}>
                                            {`(${applicableFee.fee_type} - ${
                                                feeToUse === "NOT ACCEPTED"
                                                    ? feeToUse
                                                    : `$${feeToUse}`
                                            })`}
                                            {applicableFee.fee_source ===
                                            "general" ? (
                                                <OverlayTrigger
                                                    placement="bottom"
                                                    overlay={
                                                        <Tooltip id="age-tooltip">
                                                            General Fee used
                                                        </Tooltip>
                                                    }
                                                >
                                                    <span>
                                                        <InfoIcon
                                                            width="14"
                                                            height="14"
                                                        />
                                                    </span>
                                                </OverlayTrigger>
                                            ) : null}
                                        </div>

                                        {feeDowngraded ? (
                                            <div className={styles.feeWarningText}>
                                                {`⚠ ${b.clinician_detail?.[0]?.credential} tier fee is not set. Using ${applicableFee.fee_type} instead. Please update the client's fees.`}
                                            </div>
                                        ) : null}
                                    </div>
                                </div>

                                <div className={styles.detailItem}>
                                    <div className={styles.detailIcon}>
                                        <ClockIcon />
                                    </div>
                                    <div className={styles.detailContent}>
                                        <div className={styles.detailLabel}>
                                            Time
                                        </div>
                                        <div className={styles.detailValue}>
                                            {moment(b.start).format(
                                                DateFormats.HOUR_MIN_FORMAT
                                            )}
                                            (
                                            {b.recurrenceRule
                                                ? b.recurrenceRule
                                                : null}
                                            )
                                        </div>
                                        {b?.service_delivery_option && (
                                            <div className={styles.detailSubtext}>
                                                {toTitleCase(
                                                    b?.service_delivery_option?.replaceAll(
                                                        "_",
                                                        " "
                                                    ) || ""
                                                ) || "--"}
                                            </div>
                                        )}
                                    </div>
                                </div>

                                <div className={styles.detailItem}>
                                    <div className={styles.detailIcon}>
                                        <LocationIcon />
                                    </div>
                                    <div className={styles.detailContent}>
                                        <div className={styles.detailLabel}>
                                            Location
                                        </div>
                                        <div className={styles.detailValue}>
                                            {b.location || "--"}
                                        </div>
                                        <div className={styles.detailSubtext}>
                                            Starts{" "}
                                            {moment(b.starting_date).format(
                                                DateFormats.DAY_WEEK_FORMAT
                                            )}
                                        </div>
                                    </div>
                                </div>

                                <div className={styles.detailItem}>
                                    <div className={styles.detailIcon}>
                                        <InfoIcon />
                                    </div>
                                    <div className={styles.detailContent}>
                                        <div className={styles.detailLabel}>
                                            Status
                                        </div>
                                        <div className={styles.detailSubtext}>
                                            Consult call requested:{" "}
                                            {_consultCallRequested === "Yes"
                                                ? "Yes"
                                                : "No"}
                                        </div>
                                        <div className={styles.detailSubtext}>
                                            Consult call completed:{" "}
                                            {consultCallCompleted.startsWith("Yes")
                                                ? "Yes"
                                                : "No"}
                                        </div>
                                        <div className={styles.detailSubtext}>
                                            {_vaccinatedCheckCompleted && (
                                                <div
                                                    className={styles.detailSubtext}
                                                >
                                                    Vaccination check:{" "}
                                                    {_vaccinatedCheckCompleted}
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                </div>
                            </div>

                            <div className={styles.intakeConsentWrapper}>
                                {b.supervisor_intake_session && (
                                    <div className={styles.infoBox}>
                                        <div className={styles.infoBoxHeader}>
                                            <div className={styles.infoBoxTitle}>
                                                <span>Supervisor Intake</span>
                                                {!b.supervisor_intake_session
                                                    ?.completed_at && (
                                                    <Badge
                                                        tone="warning"
                                                        content="Pending"
                                                    />
                                                )}

                                                {intakeSessionStatus ===
                                                    "completed" && (
                                                    <Badge
                                                        content={`Intake session completed on ${moment(
                                                            completedOnDate
                                                        ).format(
                                                            DateFormats.DAY_YEAR_HOUR_MIN_FORMAT
                                                        )}`}
                                                        textColor={"#205773"}
                                                        bgColor={"#C6ECFF"}
                                                    />
                                                )}
                                            </div>
                                            {intakeSessionStatus === "scheduled" && (
                                                <div
                                                    className={
                                                        styles.attendeeInfoActions
                                                    }
                                                >
                                                    <OverlayTrigger
                                                        placement="top"
                                                        overlay={
                                                            <Tooltip id="cancel-intake-tooltip">
                                                                Cancel supervisor
                                                                intake session
                                                            </Tooltip>
                                                        }
                                                    >
                                                        <Button
                                                            variant="normal"
                                                            onClick={() => {
                                                                setSelectedBookingForIntakeAction(
                                                                    b
                                                                );
                                                                setShowCancelIntakeDialog(
                                                                    true
                                                                );
                                                            }}
                                                        >
                                                            <CancelIcon />
                                                        </Button>
                                                    </OverlayTrigger>

                                                    <OverlayTrigger
                                                        placement="top"
                                                        overlay={
                                                            <Tooltip id="reschedule-intake-tooltip">
                                                                Reschedule supervisor
                                                                intake session
                                                            </Tooltip>
                                                        }
                                                    >
                                                        <Button
                                                            variant="normal"
                                                            onClick={async () => {
                                                                await loadSupervisorsWithIntake(
                                                                    b
                                                                );
                                                                setSelectedBookingForIntakeAction(
                                                                    b
                                                                );
                                                                setShowIntakeSessionDialog(
                                                                    true
                                                                );
                                                            }}
                                                        >
                                                            <EditIcon />
                                                        </Button>
                                                    </OverlayTrigger>
                                                </div>
                                            )}
                                        </div>

                                        <div className={styles.primaryActions}>
                                            <div className={styles.detailsTitle}>
                                                {
                                                    b.supervisor_intake_session
                                                        ?.clinician_display_name
                                                }{" "}
                                                ({supervisorPractice})
                                            </div>

                                            {b.supervisor_intake_session && (
                                                <div
                                                    className={styles.detailSubtext}
                                                >
                                                    <>
                                                        {moment(
                                                            b
                                                                .supervisor_intake_session
                                                                .start_time
                                                        ).format(
                                                            "ddd MMM Do, h:mma"
                                                        )}{" "}
                                                        -{" "}
                                                        {moment(
                                                            b
                                                                .supervisor_intake_session
                                                                .end_time
                                                        ).format("h:mma")}
                                                    </>
                                                    (
                                                    {
                                                        b.supervisor_intake_session
                                                            ?.location
                                                    }
                                                    )
                                                </div>
                                            )}

                                            {supervisorIntakeOveridden && (
                                                <div
                                                    className={styles.infoBoxDetail}
                                                >
                                                    Reason for skipping:{" "}
                                                    {supervisorIntakeOveridden}
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                )}
                                <div className={styles.consentBox}>
                                    <div className={styles.consentBoxTitle}>
                                        Consent details
                                        {totalConsentMissingCount > 0 && (
                                            <Badge
                                                tone="danger"
                                                content={`Consent incomplete (${totalConsentMissingCount})`}
                                            />
                                        )}
                                    </div>
                                    <div className={styles.consentTableWrapper}>
                                        <Table
                                            columns={consentColumns}
                                            data={consentTableData}
                                            allowSelect={false}
                                            variant="compact"
                                        />
                                    </div>
                                    {editingAttendeeForConfirmedDialog?.booking
                                        .id === b.id &&
                                        (() => {
                                            const { attendee, booking } =
                                                editingAttendeeForConfirmedDialog!;
                                            const missingFields =
                                                getSessionAttendeeMissingFields(
                                                    attendee,
                                                    booking.requested_service
                                                );
                                            const dob = getAttendeeDob(attendee);
                                            const dobDate = dob
                                                ? moment(dob).toDate()
                                                : null;
                                            return (
                                                <div
                                                    className={
                                                        styles.attendeeInlineForm
                                                    }
                                                >
                                                    <div
                                                        className={[
                                                            styles.attendeeFieldsGrid,
                                                            booking.requested_service ===
                                                            "Family Therapy"
                                                                ? styles.attendeeFieldsGridFive
                                                                : "",
                                                        ]
                                                            .filter(Boolean)
                                                            .join(" ")}
                                                    >
                                                        <Input
                                                            label="First name"
                                                            isRequired
                                                            value={
                                                                attendee.first_name
                                                            }
                                                            hasError={
                                                                missingFields.includes(
                                                                    "name"
                                                                ) &&
                                                                !attendee.first_name.trim()
                                                            }
                                                            errorText="Required"
                                                            onChange={(e) =>
                                                                handleSessionAttendeeChange(
                                                                    attendee.id,
                                                                    {
                                                                        first_name:
                                                                            e.target
                                                                                .value,
                                                                    }
                                                                )
                                                            }
                                                            inputSize="small"
                                                        />
                                                        <Input
                                                            label="Last name"
                                                            isRequired
                                                            value={
                                                                attendee.last_name
                                                            }
                                                            hasError={
                                                                missingFields.includes(
                                                                    "name"
                                                                ) &&
                                                                !attendee.last_name.trim()
                                                            }
                                                            errorText="Required"
                                                            onChange={(e) =>
                                                                handleSessionAttendeeChange(
                                                                    attendee.id,
                                                                    {
                                                                        last_name:
                                                                            e.target
                                                                                .value,
                                                                    }
                                                                )
                                                            }
                                                            inputSize="small"
                                                        />
                                                        <Input
                                                            label="Relationship"
                                                            value={
                                                                attendee.relationship ||
                                                                ""
                                                            }
                                                            onChange={(e) =>
                                                                handleSessionAttendeeChange(
                                                                    attendee.id,
                                                                    {
                                                                        relationship:
                                                                            e.target
                                                                                .value,
                                                                    }
                                                                )
                                                            }
                                                            inputSize="small"
                                                        />
                                                        <Input
                                                            type="email"
                                                            label="Email"
                                                            isRequired
                                                            value={
                                                                attendee.email || ""
                                                            }
                                                            hasError={missingFields.includes(
                                                                "email"
                                                            )}
                                                            errorText="Required for adults"
                                                            onChange={(e) =>
                                                                handleSessionAttendeeChange(
                                                                    attendee.id,
                                                                    {
                                                                        email: e
                                                                            .target
                                                                            .value,
                                                                    }
                                                                )
                                                            }
                                                            inputSize="small"
                                                        />
                                                        {booking.requested_service ===
                                                            "Family Therapy" && (
                                                            <DatePicker
                                                                label="Date of birth"
                                                                isRequired
                                                                dateFormat="MM/dd/yyyy"
                                                                selected={dobDate}
                                                                hasError={missingFields.includes(
                                                                    "date_of_birth"
                                                                )}
                                                                errorText="Required"
                                                                placeholderText="Date of birth"
                                                                onChange={(date) =>
                                                                    handleSessionAttendeeChange(
                                                                        attendee.id,
                                                                        {
                                                                            date_of_birth:
                                                                                date
                                                                                    ? moment(
                                                                                          date as Date
                                                                                      ).format(
                                                                                          "YYYY-MM-DD"
                                                                                      )
                                                                                    : null,
                                                                        }
                                                                    )
                                                                }
                                                                inputSize="small"
                                                            />
                                                        )}
                                                    </div>
                                                    <div
                                                        className={
                                                            styles.inlineFormActions
                                                        }
                                                    >
                                                        <Button
                                                            onClick={() =>
                                                                saveSessionAttendees(
                                                                    b.id,
                                                                    draftSessionAttendees[
                                                                        b.id
                                                                    ] || []
                                                                )
                                                            }
                                                            disabled={
                                                                attendeeSaveLoading
                                                            }
                                                        >
                                                            {attendeeSaveLoading
                                                                ? "Saving…"
                                                                : "Save"}
                                                        </Button>
                                                        <Button
                                                            variant="secondary"
                                                            onClick={() => {
                                                                const savedIds =
                                                                    new Set(
                                                                        (
                                                                            booking.session_attendees ||
                                                                            []
                                                                        ).map(
                                                                            (a) =>
                                                                                a.id
                                                                        )
                                                                    );
                                                                setDraftSessionAttendees(
                                                                    (prev) => ({
                                                                        ...prev,
                                                                        [booking.id]:
                                                                            (
                                                                                prev[
                                                                                    booking
                                                                                        .id
                                                                                ] ||
                                                                                []
                                                                            ).filter(
                                                                                (
                                                                                    a
                                                                                ) =>
                                                                                    !editingAttendeeIds.has(
                                                                                        a.id
                                                                                    ) ||
                                                                                    savedIds.has(
                                                                                        a.id
                                                                                    )
                                                                            ),
                                                                    })
                                                                );
                                                                setEditingAttendeeIds(
                                                                    new Set()
                                                                );
                                                            }}
                                                            disabled={
                                                                attendeeSaveLoading
                                                            }
                                                        >
                                                            Cancel
                                                        </Button>
                                                    </div>
                                                </div>
                                            );
                                        })()}
                                    {(supportsSessionAttendees ||
                                        canAddConfiguredGuardian) &&
                                        !editingAttendeeForConfirmedDialog && (
                                            <div
                                                className={
                                                    styles.sessionAttendeesActions
                                                }
                                            >
                                                <Button
                                                    variant="normal"
                                                    className={
                                                        styles.sendConsentFormButton
                                                    }
                                                    disabled={attendeeSaveLoading}
                                                    onClick={() =>
                                                        handleAddSessionAttendee(
                                                            b.id
                                                        )
                                                    }
                                                >
                                                    {canAddConfiguredGuardian
                                                        ? "+ Add other parent or guardian"
                                                        : "+ Add attendee"}
                                                </Button>
                                            </div>
                                        )}
                                </div>
                            </div>
                        </div>
                    );
                })
            )}
            {showChangeBookingModal && selectedBookingForChangeDetails && (
                <ChangeBookingDetails
                    client={client}
                    updateClient={updateClient}
                    booking={selectedBookingForChangeDetails}
                    appointmentState="confirmed"
                    clinician={selectedBookingForChangeDetails.clinician_detail}
                    viewFrom="appt_confirmed"
                    showEditModal={showChangeBookingModal}
                    setShowEditModal={setChangeBookingModalOpen}
                />
            )}
            <Loader show={showIntakeSessionLoader} />
            {selectedBookingForIntakeAction && (
                <SelectIntakeSessionEvaluatorDialog
                    open={showIntakeSessionDialog}
                    onClose={() => {
                        setShowIntakeSessionDialog(false);
                        setSelectedBookingForIntakeAction(null);
                        setIntakeActionReason("");
                    }}
                    supervisors={supervisors}
                    intakeAvailabilityBySupervisor={intakeAvailabilityBySupervisor}
                    selectedClinician={
                        selectedBookingForIntakeAction.clinician_detail[0]
                    }
                    defaultSupervisorId={
                        selectedBookingForIntakeAction.supervisor_intake_session
                            ?.supervisor_id || ""
                    }
                    bookingPracticeId={selectedBookingForIntakeAction.practice_id}
                    allDepartmentsAndServices={allDepartmentsAndServices}
                    onSelect={handleReschduleIntakeSessionBooking}
                    showRescheduleReason={Boolean(
                        selectedBookingForIntakeAction?.supervisor_intake_session
                    )}
                    rescheduleReason={intakeActionReason}
                    onRescheduleReasonChange={setIntakeActionReason}
                    viewFrom="appt_confirmed"
                />
            )}
            <Dialog
                open={showCancelIntakeDialog}
                onClose={() => {
                    setShowCancelIntakeDialog(false);
                    setSelectedBookingForIntakeAction(null);
                    setIntakeActionReason("");
                }}
                title="Cancel Session"
                saveText="Cancel Session"
                onSave={() => {
                    if (selectedBookingForIntakeAction) {
                        handleCancelIntakeSession(
                            selectedBookingForIntakeAction,
                            intakeActionReason
                        );
                    }
                }}
                cancelText="Close"
            >
                Are you sure you want to cancel this session? If you only need to
                change the schedule, click the <EditIcon /> icon outside of this
                modal to reschedule instead.
                <div style={{ marginTop: 16 }}>
                    <TextArea
                        label="Reason for cancellation"
                        value={intakeActionReason}
                        onChange={(e) => setIntakeActionReason(e.target.value)}
                        placeholder="Enter reason for cancellation"
                        required
                    />
                </div>
            </Dialog>
        </div>
    );
}
